import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { cursorManager } from '@/editor/manager/cursorManager';
import { CursorEnum } from '@/editor/manager/cursorManager/type';
import { modelManager } from '@/editor/manager/entityManager';
import { globalStores } from '@/editor/stores';
import { EEntityType, EOperateMode, ESceneMode } from '@/editor/types';
import { ETransformType } from '@/editor/types/entity';
import { EAutoLayoutMode } from '@/editor/utils/layout/detect';

const NOT_DRAWING_MODES: EOperateMode[] = [
  EOperateMode.EMPTY,
  EOperateMode.Layout,
  EOperateMode.Floorplan,
  EOperateMode.MultiSelect,
  EOperateMode.Alignment,
  EOperateMode.MeasureRuler,
  EOperateMode.DrawAreaDimension,
  EOperateMode.DrawLinearDimension
] as const;

export class CursorController {
  private _destroy = () => {};
  private _immovableEntities: Set<EEntityType> = new Set([EEntityType.Region, EEntityType.Room]);

  constructor() {
    editorEvent.on(EditorEventName.SCENE_MODE_CHANGE, this.updateCursor);
    editorEvent.on(EditorEventName.OPERATE_MODE_CHANGE, this.updateCursor);
    editorEvent.on(EditorEventName.ENTITY_SELECTED, this.updateCursor);
    editorEvent.on(EditorEventName.KEYBOARD_PRESS_UPDATE, this.updateCursor);

    const destroyMoving = globalStores.editorStateStore.onChange('moving', this.updateCursor);
    const destroyHoverOnEntity = globalStores.editorStateStore.onChange('hoverEntity', this.updateCursor);
    const destroyDragging = globalStores.editorStateStore.onChange('dragging', this.updateCursor);
    const destroyRotating = globalStores.editorStateStore.onChange('rotating', this.updateCursor);

    this._destroy = () => {
      editorEvent.off(EditorEventName.SCENE_MODE_CHANGE, this.updateCursor);
      editorEvent.off(EditorEventName.OPERATE_MODE_CHANGE, this.updateCursor);
      editorEvent.off(EditorEventName.ENTITY_SELECTED, this.updateCursor);

      destroyMoving();
      destroyHoverOnEntity();
      destroyDragging();
      destroyRotating();
    };
  }

  static isDrawing() {
    const operateMode = globalStores.operateInfo.mode;
    if (NOT_DRAWING_MODES.includes(operateMode)) {
      return false;
    }

    return true;
  }

  /**
   * 是否显示多选光标。
   *
   * 除了已经进入多选模式，Ctrl/⌘ 单独按住时也要显示：这两个键要等左键按下才切多选模式
   * （见 ControllerManager 里的 KEYBOARD_PRESS_UPDATE 监听），光标得在按下之前就把这件事提示出来
   */
  static isMultiSelect(): boolean {
    const { dragging, rotating, moving } = globalStores.editorStateStore;
    const isMultiSelectMode = globalStores.operateInfo.mode === EOperateMode.MultiSelect;
    // 与上面那个监听的切模式条件对齐，避免「光标提示了但按下去并不会进多选」
    const isPendingMultiSelect =
      globalStores.operateInfo.mode === EOperateMode.EMPTY &&
      !moving &&
      !dragging &&
      !rotating &&
      globalStores.keyboardStore.isMultiSelectModifierPressed();

    switch (globalStores.sceneMode) {
      case ESceneMode.scene3d: {
        return (!dragging && !rotating && isMultiSelectMode) || isPendingMultiSelect;
      }
      case ESceneMode.scene2d: {
        return isMultiSelectMode || isPendingMultiSelect;
      }
      default:
        return false;
    }
  }

  public updateCursor = () => {
    if (CursorController.isDrawing()) {
      if (globalStores.operateInfo.mode === EOperateMode.Array) {
        cursorManager.setCursor(
          globalStores.operateInfo.extraState?.arrayData?.entityType === EEntityType.DoorWindow
            ? CursorEnum.ARRAY_LINE
            : CursorEnum.ARRAY_MATRIX
        );
      } else {
        cursorManager.setCursor(CursorEnum.CROSSHAIR);
      }
      return;
    }

    if (CursorController.isMultiSelect()) {
      cursorManager.setCursor(CursorEnum.MULTI_SELECT);
      return;
    }

    const { moving, selected, hoverEntity, dragging, rotating, autoLayoutModeData } = globalStores.editorStateStore;
    const isAutoLayoutMode = globalStores.editorStateStore.autoLayoutMode !== EAutoLayoutMode.NONE;

    if (isAutoLayoutMode) {
      switch (hoverEntity.type) {
        case EEntityType.DoorWindow: {
          if (!autoLayoutModeData.allowedDoorIds.includes(hoverEntity.entityId)) {
            cursorManager.setCursor(CursorEnum.NOT_ALLOWED);
          } else {
            cursorManager.setCursor(CursorEnum.DEFAULT);
          }
          break;
        }
        case EEntityType.Room: {
          cursorManager.setCursor(CursorEnum.DEFAULT);
          break;
        }
        case EEntityType.Region: {
          cursorManager.setCursor(CursorEnum.NOT_ALLOWED);
          break;
        }
        default: {
          cursorManager.setCursor(CursorEnum.DEFAULT);
        }
      }

      return;
    }

    // 测量模式
    const operateMode = globalStores.operateInfo.mode;
    if (
      [EOperateMode.DrawAreaDimension, EOperateMode.MeasureRuler, EOperateMode.DrawLinearDimension].includes(
        operateMode
      )
    ) {
      switch (operateMode) {
        case EOperateMode.DrawAreaDimension:
          cursorManager.setCursor(CursorEnum.DRAW_AREA_DIMENSION);
          break;
        case EOperateMode.DrawLinearDimension:
          cursorManager.setCursor(CursorEnum.DRAW_LINEAR_DIMENSION);
          break;
        case EOperateMode.MeasureRuler:
          cursorManager.setCursor(CursorEnum.MEASURE);
          break;
        default:
          break;
      }

      return;
    }

    // 面积标注变换组件
    if (hoverEntity.type === EEntityType.AreaTransform) {
      this._updateAreaTransformCursor();
      return;
    }

    // 形变控件
    if (hoverEntity.transformType && hoverEntity.transformType !== ETransformType.Empty) {
      if (hoverEntity.transformType === ETransformType.Rotation) {
        cursorManager.setCursor(CursorEnum.MOVE);
        return;
      }

      const { rotation, width, height } = modelManager.transformManager.getData();
      let offset = 0;
      switch (hoverEntity.transformType) {
        case ETransformType.ScaleRightTop:
        case ETransformType.ScaleLeftBottom:
          offset = Math.atan(height / width);
          break;
        case ETransformType.ScaleRightBottom:
        case ETransformType.ScaleLeftTop:
          offset = -Math.atan(height / width);
          break;
        case ETransformType.ScaleTop:
        case ETransformType.ScaleBottom:
          offset = Math.PI / 2;
          break;
        case ETransformType.ScaleLeft:
        case ETransformType.ScaleRight:
          offset = 0;
          break;
      }

      cursorManager.setCursor(CursorEnum.RESIZE_HORIZONTAL, -(rotation + offset));
      return;
    }

    // 墙端点 hover 即可直接拖动，光标要提示可移动；
    // 墙体/隔墙/边界已改为选中后才能拖动，hover 时与其他构件一样走下面的默认（选择）光标
    // 隐藏的构件已从场景中移除，不会被 hover 到，这里无需再判断墙体整体显隐
    if (hoverEntity.type === EEntityType.WallExtremePoint) {
      cursorManager.setCursor(CursorEnum.MOVE);
      return;
    }

    if (
      moving &&
      hoverEntity.entityId &&
      hoverEntity.entityId === selected.entityId &&
      !this._immovableEntities.has(hoverEntity.type)
    ) {
      cursorManager.setCursor(CursorEnum.MOVE);
      return;
    }

    // 光标要跟着 OrbitControls 的平移/旋转互换走：默认平移的键位按住修饰键后变成旋转，反之亦然
    const isOrbitSwapped = globalStores.keyboardStore.isOrbitSwapModifierPressed();

    if (dragging) {
      cursorManager.setCursor(isOrbitSwapped ? CursorEnum.ALL_SCROLL : CursorEnum.GRABBING);
      return;
    }

    if (rotating) {
      cursorManager.setCursor(isOrbitSwapped ? CursorEnum.GRABBING : CursorEnum.ALL_SCROLL);
      return;
    }

    cursorManager.setCursor(CursorEnum.DEFAULT);
  };

  private _updateAreaTransformCursor() {
    const hoverEntity = globalStores.editorStateStore.hoverEntity;
    if (hoverEntity.entityId !== ETransformType.Empty) {
      const { controlPointMap } = modelManager.transformManager.getAreaTransformData();
      const offset = controlPointMap.get(hoverEntity.entityId);
      cursorManager.setCursor(CursorEnum.RESIZE_HORIZONTAL, offset ?? 0);
    }
  }

  destroy() {
    cursorManager.setCursor(CursorEnum.DEFAULT);
    this._destroy();
  }
}
